import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import config from '@plone/volto/registry';
import cx from 'classnames';
import SidebarPortal from '@plone/volto/components/manage/Sidebar/SidebarPortal';
import BlockDataForm from '@plone/volto/components/manage/Form/BlockDataForm';
import { SlateEditor } from '@plone/volto-slate/editor';
import { handleKey } from '@plone/volto-slate/blocks/Text/keyboard';
import logo from './images/moniviestin_logo_small.svg';
import styles from './styles.module.scss';
import EmbedPlayer from '../../Embed/EmbedPlayer';

const messages = defineMessages({
  blockTitle: {
    id: 'featuredVideoBlockTitle',
    defaultMessage: 'Featured video',
  },
  media: {
    id: 'featuredVideoMedia',
    defaultMessage: 'Media',
  },
  placeholderText: {
    id: 'featuredVideoPlaceholderText',
    defaultMessage: 'Type text...',
  },
  textPosition: {
    id: 'featuredVideoTextPosition',
    defaultMessage: 'Text position',
  },
  textPositionTop: {
    id: 'featuredVideoTextPositionTop',
    defaultMessage: 'Top',
  },
  textPositionBottom: {
    id: 'featuredVideoTextPositionBottom',
    defaultMessage: 'Bottom',
  },
});

const schema = (intl) => ({
  title: intl.formatMessage(messages.blockTitle),
  fieldsets: [
    {
      id: 'default',
      title: 'Default',
      fields: ['href', 'textPosition'],
    },
  ],
  properties: {
    href: {
      title: intl.formatMessage(messages.media),
      widget: 'object_browser',
      mode: 'link',
      selectedItemAttrs: ['UID', 'Title'],
    },
    textPosition: {
      title: intl.formatMessage(messages.textPosition),
      choices: [
        ['top', intl.formatMessage(messages.textPositionTop)],
        ['bottom', intl.formatMessage(messages.textPositionBottom)],
      ],
      default: 'top',
    },
  },
  required: ['href'],
});

const Edit = (props) => {
  const {
    data = {},
    onChangeBlock,
    block,
    selected,
    index,
    properties,
    onSelectBlock,
  } = props;
  const intl = useIntl();
  const { slate } = config.settings;
  const { value } = data;
  const uid = data.href?.[0]?.UID;
  const textPosition = data.textPosition || 'top';

  const placeholder = intl.formatMessage(messages.placeholderText);

  const withBlockProperties = React.useCallback(
    (editor) => {
      editor.getBlockProps = () => props;
      return editor;
    },
    [props],
  );

  const handleFocus = React.useCallback(() => {
    onSelectBlock(block);
  }, [block, onSelectBlock]);

  const handleChange = React.useCallback(
    (newValue) => {
      onChangeBlock(block, {
        ...data,
        value: newValue,
      });
    },
    [block, data, onChangeBlock],
  );

  return (
    <>
      <div
        className={cx(
          'block',
          'featured-video-block',
          styles.featuretextblock,
          styles.editMode,
          { [styles.textBottom]: textPosition === 'bottom' },
        )}
      >
        <div className={styles.textcontent}>
          <img className={styles.logoimage} src={logo} alt="Moniviestin" />
          <div className={styles.text}>
            <SlateEditor
              index={index}
              properties={properties}
              extensions={slate.textblockExtensions}
              renderExtensions={[withBlockProperties]}
              value={value}
              block={block}
              onChange={handleChange}
              onKeyDown={handleKey}
              selected={selected}
              onFocus={handleFocus}
              placeholder={placeholder}
              slateSettings={config.settings.slate}
            />
          </div>
        </div>
        {uid && (
          <div className={styles.embed}>
            <EmbedPlayer uid={uid} type="video" />
          </div>
        )}
      </div>

      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema(intl)}
          title={schema(intl).title}
          onChangeField={(id, value) =>
            onChangeBlock(block, { ...data, [id]: value })
          }
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

export default Edit;
